import { ListColumn } from "./types";

/**
 * Merge a user's saved column order onto the coded column set.
 *
 * The saved array is a full ordered key list at save time, but code moves on:
 * columns get removed, org-gated columns come and go, and new columns are added
 * between existing ones. The merge therefore:
 *
 * 1. Drops saved keys that no longer exist in `coded` (removed or unavailable
 *    this render), and any duplicates.
 * 2. Inserts coded columns missing from the saved list right after their nearest
 *    preceding coded neighbour that is present — so a column added in code
 *    between `city` and `created` lands between them for existing users instead
 *    of at the end. A missing column with no present predecessor goes to the front.
 * 3. Forces the pinned column (the row anchor, e.g. Namn / Anst.nr / Ordernr) to
 *    index 0 regardless of what was saved.
 *
 * `coded` is expected in coded declaration order and already filtered by
 * availability. With no saved order (or an empty one) the coded order is returned.
 */
export function applyColumnOrder<T>(
	coded: ListColumn<T>[],
	saved?: string[] | null
): ListColumn<T>[] {
	const byKey = new Map(coded.map((c) => [c.key, c]));

	// 1. Saved keys, minus the ones that no longer exist, minus duplicates.
	const orderedKeys: string[] = [];
	for (const key of saved ?? []) {
		if (byKey.has(key) && !orderedKeys.includes(key)) orderedKeys.push(key);
	}

	// 2. Splice in coded columns the saved order does not know about, each after
	//    the previous coded column (which is present by construction once handled).
	let anchor = -1;
	for (const col of coded) {
		const index = orderedKeys.indexOf(col.key);
		if (index >= 0) {
			anchor = index;
			continue;
		}
		anchor += 1;
		orderedKeys.splice(anchor, 0, col.key);
	}

	// 3. The pinned column is always leftmost.
	const pinned = coded.find((c) => c.pinned);
	if (pinned) {
		const index = orderedKeys.indexOf(pinned.key);
		if (index > 0) {
			orderedKeys.splice(index, 1);
			orderedKeys.unshift(pinned.key);
		}
	}

	return orderedKeys.map((key) => byKey.get(key) as ListColumn<T>);
}

/**
 * Whether an ordered key list is exactly the coded order — in which case the
 * saved preference is redundant and gets dropped (same "store only deviations"
 * philosophy as the visibility and width overrides).
 */
export function isDefaultOrder<T>(coded: ListColumn<T>[], orderedKeys: string[]): boolean {
	if (orderedKeys.length !== coded.length) return false;
	return coded.every((col, i) => col.key === orderedKeys[i]);
}
